import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks/outsideClick";
import AppSelect from "../AppSelect";
import AppButton from "../AppButton";
import AppToggle from "../AppToggle";
import { fetcher } from "../../services/api";
import { DayPicker } from "react-day-picker";
import moment from "moment-timezone";

import "./AppFilters.css";
import "react-day-picker/dist/style.css";

const typesOptions = [
  { value: 1, name: "GIFs" },
  { value: 2, name: "Clips" },
  { value: 3, name: "Stickers" },
  { value: 5, name: "Memes" },
  { value: 0, name: "N/A" },
];

export interface Client {
  id: number;
  display_name: string;
}
export interface Country {
  country_code: string;
  display_name: string;
}
type DateRangeMode = "Day" | "Week" | "Month" | "Custom";

interface AppFiltersProps {
  isAnalyticalView: boolean;
  setIsAnalyticalView: (val: boolean) => void;
  selectedFilters: {
    types: number[];
    clients: number[];
    countries: string[];
    date_from: string;
    date_to: string;
  };
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<AppFiltersProps["selectedFilters"]>
  >;
  dateRangeMode: DateRangeMode;
  setDateRangeMode: (val: DateRangeMode) => void;
}

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function AppFilters({
  isAnalyticalView,
  setIsAnalyticalView,
  selectedFilters,
  setSelectedFilters,
  dateRangeMode,
  setDateRangeMode,
}: AppFiltersProps) {
  const [clientsData, setClientsData] = useState<Client[]>([]);
  const [countriesData, setCountriesData] = useState<Country[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    from?: Date;
    to?: Date;
  }>({});
  const dayPickerRef = useRef<HTMLDivElement | null>(null);

  const clientsOptions = useMemo(
    () =>
      clientsData.map(({ id, display_name }) => ({
        value: id,
        name: display_name,
      })),
    [clientsData]
  );
  const countryOptions = useMemo(() => {
    return countriesData
      .slice()
      .sort((a, b) => a.display_name.localeCompare(b.display_name))
      .map(({ country_code, display_name }) => ({
        value: country_code,
        name: display_name,
      }));
  }, [countriesData]);

  const updateFilter = useCallback(
    <T,>(key: keyof typeof selectedFilters, values: T[]) => {
      setSelectedFilters((prev) => {
        if (JSON.stringify(prev[key]) === JSON.stringify(values)) return prev;
        return { ...prev, [key]: values };
      });
    },
    [setSelectedFilters]
  );

  const updateDateRange = (mode: DateRangeMode) => {
    setDateRangeMode(mode);
    if (mode === "Custom") return;
    const date_to = moment().format("YYYY-MM-DD");
    let date_from = date_to;

    switch (mode) {
      case "Week":
        date_from = moment().subtract(1, "week").format("YYYY-MM-DD");
        break;
      case "Month":
        date_from = moment().subtract(1, "month").format("YYYY-MM-DD");
        break;
      case "Day":
        break;
    }
    setSelectedFilters((prev) => ({ ...prev, date_from, date_to }));
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetcher<{ data: { clients: Client[] } }>(
          "/publisher/analytic/clients_list"
        );
        const clients = res?.data?.data?.clients || [];
        setClientsData(clients);
        setSelectedFilters((prev) => ({
          ...prev,
          types: prev.types.length
            ? prev.types
            : typesOptions.map((t) => t.value),
          clients: prev.clients.length
            ? prev.clients
            : clients.map((c) => c.id),
        }));
      } catch (err) {
        console.error("Failed to fetch clients:", err);
      }
    };
    fetchClients();
  }, [setSelectedFilters]);

  useEffect(() => {
    const fetchCountries = async () => {
      if (!selectedFilters.types.length || !selectedFilters.clients.length)
        return;
      try {
        const query = new URLSearchParams({
          types: selectedFilters.types.join(","),
          clients: selectedFilters.clients.join(","),
          date_from: selectedFilters.date_from,
          date_to: selectedFilters.date_to,
        });
        const res = await fetcher<{ data: Country[] }>(
          `/publisher/analytic/countries_list?${query}`
        );
        const countryList = res?.data?.data || [];
        setCountriesData(countryList);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, [
    selectedFilters.types,
    selectedFilters.clients,
    selectedFilters.date_from,
    selectedFilters.date_to,
    setSelectedFilters,
  ]);

  useEffect(() => {
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev };
      updatedFilters.countries = countriesData.map((c) => c.country_code);
      if (prev.clients.length === 0 && clientsData.length) {
        updatedFilters.clients = clientsData.map((c) => c.id);
      }
      if (prev.types.length === 0) {
        updatedFilters.types = typesOptions.map((t) => t.value);
      }
      return updatedFilters;
    });
  }, [
    countriesData,
    selectedFilters.clients.length,
    selectedFilters.types.length,
    clientsData,
    setSelectedFilters,
  ]);

  useOutsideClick(dayPickerRef, () => {
    if (isDatePickerOpen) setIsDatePickerOpen(false);
  });

  return (
    <div className="app-filters">
      <div className="app-filters_left">
        <AppSelect
          options={typesOptions}
          placeholder="All Types"
          numerical
          value={selectedFilters.types}
          onSelect={(values) => {
            if (values.length > 0) {
              updateFilter("types", values as number[]);
            } else {
              updateFilter(
                "types",
                typesOptions.map((i) => i.value) as number[]
              );
            }
          }}
        />
        <AppSelect
          options={clientsOptions}
          placeholder="All Apps"
          value={selectedFilters.clients}
          numerical
          onSelect={(values) => {
            if (values.length > 0) {
              updateFilter("clients", values as number[]);
            } else {
              updateFilter(
                "clients",
                clientsOptions.map((i) => i.value) as number[]
              );
            }
          }}
        />
        <AppSelect
          options={countryOptions}
          placeholder="All Countries"
          value={selectedFilters.countries}
          numerical
          searchable
          formatOptionLabel={(option: any) => (
            <span
              title={
                option.name === "Undefined"
                  ? "Country data not provided in the request"
                  : ""
              }
            >
              {option.name}
            </span>
          )}
          onSelect={(values) => {
            if (values.length > 0) {
              updateFilter("countries", values as string[]);
            } else {
              updateFilter(
                "countries",
                countryOptions.map((i) => i.value) as string[]
              );
            }
          }}
        />
      </div>
      <div className="app-filters_right">
        {(["Day", "Week", "Month", "Custom"] as const).map((label) => (
          <AppButton
            key={label}
            variant="primary"
            ariaLabel={label}
            active={dateRangeMode === label}
            onClick={() => {
              updateDateRange(label);
              if (label === "Custom") {
                setIsDatePickerOpen((prev) => {
                  if (!prev) {
                    setCustomDateRange({
                      from: selectedFilters.date_from
                        ? new Date(selectedFilters.date_from)
                        : undefined,
                      to: selectedFilters.date_to
                        ? new Date(selectedFilters.date_to)
                        : undefined,
                    });
                  }
                  return !prev;
                });
                setIsAnalyticalView(false);
              } else {
                setIsDatePickerOpen(false);
              }
            }}
          >
            {label}
          </AppButton>
        ))}
        <AppToggle
          text="Charts"
          disabled={dateRangeMode === "Custom"}
          checked={isAnalyticalView && dateRangeMode !== "Custom"}
          onChange={() => setIsAnalyticalView(!isAnalyticalView)}
        />
      </div>

      {dateRangeMode === "Custom" && isDatePickerOpen && (
        <div ref={dayPickerRef} className="datepicker-wrapper">
          <DayPicker
            timeZone="Asia/Tbilisi"
            mode="range"
            selected={
              customDateRange.from || customDateRange.to
                ? { from: customDateRange.from, to: customDateRange.to }
                : undefined
            }
            defaultMonth={customDateRange.from || new Date()}
            numberOfMonths={2}
            onSelect={(range) => {
              setCustomDateRange(range || {});
              if (range?.from && range?.to) {
                const format = formatDateLocal;
                setSelectedFilters((prev) => ({
                  ...prev,
                  date_from: format(range.from ?? new Date()),
                  date_to: format(range.to ?? new Date()),
                }));
              }
            }}
            disabled={{ after: new Date() }}
          />
        </div>
      )}
    </div>
  );
}
